// Type-only (erased at compile) so the two model files can reference each other
// without a runtime import cycle.
import type { DisbursementRoundStatusCode } from './disbursement-round.model';

// Models for the Disbursement Document Certification module (ขั้นตอนการรับรองเอกสารส่งเบิก).
// v2.0 phase 1 only (ADR-BU) — context/decisions/DECISION_DisbursementV20.md. Phase 2 (the round
// itself) lives in disbursement-round.model.ts.
export type DisbursementStatusCode =
  | 'pending_teacher'
  | 'pending_finance'
  | 'pending_round'
  | 'completed'
  | 'revision_schedule_requested'
  | 'conditions_not_met';

export const DISBURSEMENT_STATUS_LABELS: Record<DisbursementStatusCode, string> = {
  pending_teacher: 'รออาจารย์รับรอง',
  pending_finance: 'รอเจ้าหน้าที่การเงินรับรอง',
  pending_round: 'รอเข้ารอบส่งเบิก',
  completed: 'เสร็จสิ้น',
  revision_schedule_requested: 'ส่งกลับแก้ไข',
  // Terminal: unified re-entry recalculated the cert (after a schedule correction) and
  // the teacher no longer meets conditions — never becomes pending again.
  conditions_not_met: 'ไม่ผ่านเงื่อนไข',
};

// Mirrors DisbursementCertification::POOL_STATUS_CODE / FINAL_STATUS_CODE.
export const DISBURSEMENT_POOL_STATUS_CODE: DisbursementStatusCode = 'pending_round';
export const DISBURSEMENT_FINAL_STATUS_CODE: DisbursementStatusCode = 'completed';

// "Finished phase 1 and beyond" — the same pair the API's `scope=completed` filter uses.
// Controls document VISIBILITY only (preview is allowed from pending_round onward); actual
// DOWNLOAD stays locked until DISBURSEMENT_FINAL_STATUS_CODE (registry-detail's downloadsUnlocked()).
export const DISBURSEMENT_COMPLETED_STATUS_CODES: DisbursementStatusCode[] = [
  DISBURSEMENT_POOL_STATUS_CODE,
  DISBURSEMENT_FINAL_STATUS_CODE,
];

export type DisbursementPath = 'regular' | 'international';

export const DISBURSEMENT_PATH_LABELS: Record<DisbursementPath, string> = {
  regular: 'ภาคปกติ',
  international: 'ภาคนานาชาติ',
};

export interface DisbursementAcademicTerm {
  id: number;
  name: string;
  year: number;
}

export interface DisbursementDepartment {
  id: number;
  name?: string | null;
  name_th?: string | null;
  name_en?: string | null;
  // Faculty-maintained prefill for the ใบปะหน้า's โทร. field — seeds the officer's editable
  // input only; what prints is whatever the round itself stored.
  contact_phone?: string | null;
}

export interface DisbursementUser {
  id: number;
  full_name: string;
  email?: string | null;
}

export interface DisbursementSubjectRef {
  id: number;
  code: string;
  name: string;
}

export interface DisbursementCertificationTeacher {
  user_id: number;
  full_name?: string | null;
  certified_at?: string | null;
  required?: boolean;
}

export interface DisbursementCompensationSnapshot {
  total_request_count?: number;
  approved_request_count?: number;
  pending_request_count?: number;
  total_requested_hours?: number;
  approved_hours?: number;
  pending_hours?: number;
  minimum_teaching_workload?: number;
  teaching_workload?: number;
  makeup_teaching_workload?: number;
  total_teaching_workload?: number;
  excess_hours?: number;
  academic_term?: DisbursementAcademicTerm | null;
  erp_comparison?: unknown;
}

export type DisbursementDocType = 'doc_1' | 'doc_2' | 'doc_3' | 'doc_4' | 'doc_5' | 'doc_6' | 'doc_7' | 'merged' | 'cover_memo';

export interface DisbursementDocumentEntry {
  id: number;
  // Intentionally loose `string` to allow backend literals beyond the known enum (e.g. "round_merged").
  document_type: string;
  generated_at?: string | null;
  byte_size?: number | null;
  // For international certs: per-teacher doc instances (doc_3, doc_7) carry the teacher id; null for regular certs and once-per-course docs.
  teacher_user_id?: number | null;
}

// Thai labels for the two-section layout: "เอกสารส่งเบิกที่คำนวณแล้ว" (doc_2+doc_5 or doc_3+doc_4),
// "ตารางสอน" (doc_7). doc_6 is only inside merged PDF, never a separate row.
// doc_1/doc_7 renamed everywhere per the SIT sprint 2 glossary (2026-08-20).
export const DISBURSEMENT_DOC_LABELS: Record<string, string> = {
  doc_1: 'บันทึกขออนุมัติตารางสอน',
  doc_2: 'ตารางการคำนวณค่าสอนพิเศษเกินภาระงานสอน (ภาคปกติ)',
  doc_5: 'หลักฐานการเบิกจ่ายเงินค่าสอนพิเศษ (ภาคปกติ)',
  doc_3: 'รายงานภาระการสอน (ภาคนานาชาติ)',
  doc_4: 'หลักฐานการจ่ายเงินค่าตอบแทนการสอน (ภาคนานาชาติ)',
  doc_7: 'ตารางสอน',
  doc_6: 'ใบปะหน้า/บันทึกข้อความ',
  merged: 'เอกสารรวมพร้อมส่งเบิก',
  cover_memo: 'ใบปะหน้า (บันทึกข้อความเบิกจ่ายค่าตอบแทนการสอน)',
  round_merged: 'เอกสารส่งเบิกฉบับรวม',
};

// Doc types per path — never mix paths (a cert's sections show only its own path's docs).
export const DISBURSEMENT_CALC_DOC_TYPES: Record<DisbursementPath, DisbursementDocType[]> = {
  regular: ['doc_2', 'doc_5'],
  international: ['doc_3', 'doc_4'],
};

export const DISBURSEMENT_SCHEDULE_DOC_TYPES: DisbursementDocType[] = ['doc_7'];

// The canonical per-cert document list (title + order) — registry-detail renders it as-is;
// the ออกใบปะหน้า dialog renders it without `merged` (that file only exists once the round
// is saved). Keep the two in one place so a doc added here shows up in both.
export interface DisbursementDocSectionSpec {
  title: string;
  docType: DisbursementDocType;
}

export const DISBURSEMENT_DOC_SECTIONS: Record<DisbursementPath, DisbursementDocSectionSpec[]> = {
  regular: [
    { title: 'ตารางสอน', docType: 'doc_7' },
    { title: 'เอกสารคำนวณภาระงาน', docType: 'doc_2' },
    { title: 'หลักฐานการเบิกจ่ายเงินค่าสอนพิเศษและค่าสอนเกินภาระงาน', docType: 'doc_5' },
    { title: 'บันทึกข้อความ', docType: 'doc_1' },
    { title: 'ไฟล์รวม', docType: 'merged' },
  ],
  international: [
    { title: 'ตารางสอน', docType: 'doc_7' },
    { title: 'รายงานภาระการสอน', docType: 'doc_3' },
    { title: 'หลักฐานการจ่ายเงิน', docType: 'doc_4' },
    { title: 'บันทึกข้อความ', docType: 'doc_1' },
    { title: 'ไฟล์รวม', docType: 'merged' },
  ],
};

export function disbursementDocSections(
  path: DisbursementPath | null | undefined,
): DisbursementDocSectionSpec[] {
  return DISBURSEMENT_DOC_SECTIONS[path === 'international' ? 'international' : 'regular'];
}

// Same list without the ไฟล์รวม row — for callers that can only render per-doc files.
export function disbursementDocSectionsWithoutMerged(
  path: DisbursementPath | null | undefined,
): DisbursementDocSectionSpec[] {
  return disbursementDocSections(path).filter((spec) => spec.docType !== 'merged');
}

// On หลักสูตรนานาชาติ certs these two are generated once per member teacher, so every
// request for them carries a teacher_user_id; regular certs have a single instance.
export function isPerTeacherDisbursementDoc(docType: DisbursementDocType): boolean {
  return docType === 'doc_3' || docType === 'doc_7';
}

export interface DisbursementRevisionAction {
  action_code?: string | null;
  actor_role_code?: string | null;
  comment?: string | null;
  acted_at?: string | null;
  actor?: {
    id?: number | null;
    full_name?: string | null;
    email?: string | null;
  } | null;
}

export interface DisbursementApprovalRequest {
  id?: number;
  status_code?: string | null;
  current_status?: unknown;
  active_blockers?: unknown[];
  latest_revision_action?: DisbursementRevisionAction | null;
}

// Per-station entry for the approval stepper; `state` drives styling, `acted_at` only set for `done`.
export type DisbursementApprovalTrackState = 'done' | 'current' | 'waiting';

export interface DisbursementTeacherProgress {
  user_id: number;
  name?: string | null;
  certified_at?: string | null;
  // false for a ฿0 co-teacher: listed on the document, but the chain does not wait for them.
  required?: boolean;
}

export interface DisbursementApprovalTrackEntry {
  station: string;
  role_label: string;
  person_names: string[];
  state: DisbursementApprovalTrackState;
  acted_at?: string | null;
  // Only on teacher station of international certs; drives N certify sub-rows and progress.
  teacher_progress?: DisbursementTeacherProgress[];
}

export interface DisbursementCoverMemoSummary {
  id: number;
  document_number?: string | null;
  issued_on?: string | null;
  has_pdf: boolean;
}

export interface DisbursementCertificationModel {
  id: number;
  locked_at?: string | null;
  revision_comment?: string | null;
  // Set on send-back (cleared once resolved); indicates the station to resume/advance from.
  revision_requested_from_status?: DisbursementStatusCode | null;
  compensation_snapshot?: DisbursementCompensationSnapshot | null;
  teacher_notified_at?: string | null;
  academic_term: DisbursementAcademicTerm;
  // Null for international (per-course) certs; non-null for regular per-teacher certs.
  user: DisbursementUser | null;
  // Null for regular certs.
  subject?: DisbursementSubjectRef | null;
  // Null for regular certs.
  section?: string | null;
  // Empty for regular certs.
  teachers?: DisbursementCertificationTeacher[];
  department: DisbursementDepartment;
  path: DisbursementPath;
  path_label: string;
  status_code: DisbursementStatusCode;
  status_label: string;
  // 14-day window per cert for pending_teacher; pending_finance has no deadline (v2.0 ADR-DI).
  revision_allowed: boolean;
  revision_deadline?: string | null;
  cover_memo?: DisbursementCoverMemoSummary | null;
  // Presence flag for doc_1 (overload memo); `has_pdf: false` renders empty state (endpoint 404s if absent).
  overload_memo?: { has_pdf: boolean } | null;
  // Controller-layer flags (not stored): in cover-memo round, round id, window closed status.
  in_round?: boolean;
  cover_memo_id?: number | null;
  // v2.0 round summary — where the cert's รอบส่งเบิก stands (see disbursement-round.model.ts).
  // `show` only (registry-detail's round section) — absent on list/index rows.
  round_status_code?: DisbursementRoundStatusCode | null;
  round_status_label?: string | null;
  round_no?: number | null;
  round_submitted_at?: string | null;
  round_finance_signed_at?: string | null;
  round_head_signed_at?: string | null;
  round_vice_dean_signed_at?: string | null;
  round_completed_at?: string | null;
  round_returned_at?: string | null;
  round_send_back_comment?: string | null;
  window_closed?: boolean;
  documents: DisbursementDocumentEntry[];
  approval_request?: DisbursementApprovalRequest | null;
  approval_track: DisbursementApprovalTrackEntry[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface DisbursementCertificationListParams {
  academic_term_id?: number | string | null;
  status?: DisbursementStatusCode | null;
  // 'pending' = ทุกสถานะที่ยังเดินอยู่ในสาย (รวม pending_round) — แท็บ "รอดำเนินการ" ของ role
  // ที่ไม่ได้ยืนอยู่สถานีใดสถานีหนึ่ง มิฉะนั้นแท็บนั้นจะได้ผลเท่ากับ "ทั้งหมด"
  scope?: 'completed' | 'pending' | null;
  // แกนคนละแกนกับ scope — เจ้าของใบ (mine) กับใบของคนอื่น (others) ต้อง compose กับ
  // scope/status ได้ ใช้กับแท็บบนสุดของผู้บริหารที่สอนจริง
  ownership?: 'mine' | 'others' | null;
  department_id?: number | string | null;
  keywords?: string | null;
  // 'no_department' = ค้นเฉพาะชื่อคน/ชื่อวิชา ไม่จับชื่อสาขา — หน้า registry ใช้ (สาขามี filter แยกอยู่แล้ว)
  keyword_scope?: 'no_department' | null;
  page?: number;
  limit?: number;
  sort?: 'status' | 'updated_at' | 'teacher_name' | 'department' | null;
  direction?: 'asc' | 'desc' | null;
}

export interface DisbursementCertificationListResponse {
  data: DisbursementCertificationModel[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export type DisbursementRevisionApprovableType =
  | 'ScheduleSubmissionSubjectSchedule'
  | 'ProjectResearchSubjectAssignment';

export interface DisbursementRevisionTeachingSlot {
  day_of_week?: number | null;
  start_time?: string | null;
  end_time?: string | null;
  room_name?: string | null;
}

// One selectable row of the send-back picker (Disbursement::SendBackService.candidate_row) —
// an already-approved course/project of the sending cert's own schedule submission.
// For a regular `is_workload_split` course, the UI submits only this assignment; the
// correction API expands the selected assignment to every assignment in its shared group.
export interface DisbursementRevisionCandidateRow {
  approvable_type: DisbursementRevisionApprovableType;
  approvable_id: number;
  status_code?: string | null;
  status_label?: string | null;
  subject_code?: string | null;
  subject_name?: string | null;
  section?: string | null;
  teaching_type?: string | null;
  is_workload_split?: boolean;
  teaching_slots?: DisbursementRevisionTeachingSlot[];
}

// GET revision_candidates — an international (per-course) cert has no single submission to
// pick from, so `show_picker` is false and `candidates` stays empty (reason-only dialog).
// Regular shared-workload certs remain picker-based; their rows carry `is_workload_split` so
// the dialog can explain the group-level correction and cover-memo boundary before submit.
export interface DisbursementRevisionCandidatesResponse {
  claim_shape: string;
  show_picker: boolean;
  schedule_submission_id?: number | null;
  candidates: DisbursementRevisionCandidateRow[];
}

export interface DisbursementRequestRevisionItem {
  approvable_type: DisbursementRevisionApprovableType;
  approvable_id: number;
  teacher_note: string;
}

export interface DisbursementRequestRevisionPayload {
  comment: string;
  // Omitted (or empty) for an international-course cert's reason-only send-back.
  items?: DisbursementRequestRevisionItem[];
  new_course_request_note?: string | null;
}

export interface DisbursementTestProvisionResponse {
  triggered: boolean;
  certifications_count: number;
}

export type DisbursementRosterKind = 'teacher' | 'course';

// v2.0 phase vocabulary (Disbursement::RegistryRoster#phase_for) — context/reference/GLOSSARY_Disbursement.md §3
export type DisbursementRosterPhase =
  | 'readiness'
  | 'waiting_teacher'
  | 'waiting_finance'
  | 'in_revision'
  | 'disqualified'
  | 'ready_for_round'
  | 'in_round'
  | 'completed'
  | 'missed'
  | 'expired';

export interface DisbursementRosterRow {
  kind: DisbursementRosterKind;
  user: DisbursementUser | null;
  subject: DisbursementSubjectRef | null;
  section?: string | null;
  department: DisbursementDepartment;
  phase: DisbursementRosterPhase;
  readiness_missing: string[];
  status_code: DisbursementStatusCode | null;
  certification_id: number | null;
  in_round: boolean;
  cover_memo_id: number | null;
  round_no: number | null;
  round_status_code: DisbursementRoundStatusCode | null;
}

export interface DisbursementRosterResponse {
  rows: DisbursementRosterRow[];
}

export interface DisbursementRosterParams {
  academic_term_id: number | string;
}
